import React, { useState } from "react";
import { Wrench, Plus, Trash2 } from "lucide-react";
import { Input, Select, Textarea, Button, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { PRICE_TYPES } from "./constants";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

const empty = { name: "", description: "", price_type: "range", minimum_price: "", maximum_price: "", duration_minutes: "60" };

export function StepServices({ state, reload, goNext, goBack }: StepProps) {
  const [form, setForm] = useState({ ...empty });
  const [adding, setAdding] = useState(false);
  const services = state.services ?? [];
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (form.name.trim().length < 1) { toast.error("Give the service a name."); return; }
    setAdding(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(), price_type: form.price_type,
        duration_minutes: Number(form.duration_minutes) || 60,
      };
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.minimum_price) payload.minimum_price = Number(form.minimum_price);
      if (form.maximum_price) payload.maximum_price = Number(form.maximum_price);
      await api.addService(payload);
      await reload();
      setForm({ ...empty });
      toast.success("Service added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id: string) => {
    try { await api.deleteService(id); await reload(); toast.success("Service removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StepShell
      icon={Wrench}
      title="What services do you offer?"
      description="Add the jobs your AI can discuss, quote and book. You can refine these later."
      onBack={goBack}
      onContinue={goNext}
      continueLabel={services.length ? "Save & continue" : "Skip for now"}
    >
      {services.length > 0 && (
        <div className="space-y-2" data-testid="services-list">
          {services.map((s) => (
            <div key={s.id as string} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3"
              data-testid={`service-item-${s.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{s.name as string}</p>
                <p className="text-xs text-slate-500">
                  {s.duration_minutes as number} min · {String(s.price_type)}
                  {s.minimum_price != null && ` · from $${s.minimum_price}`}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(s.id as string)} data-testid={`service-delete-${s.id}`}>
                <Trash2 className="h-4 w-4 text-slate-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
          <Plus className="h-4 w-4" /> Add a service
        </div>
        <div className="space-y-4">
          <Field label="Service name" testid="field-svc-name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. AC repair" data-testid="svc-name" />
          </Field>
          <Field label="Description" testid="field-svc-desc">
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="What's included?" data-testid="svc-desc" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Pricing" testid="field-svc-price-type">
              <Select value={form.price_type} onChange={(v) => set("price_type", v)} options={PRICE_TYPES} testid="svc-price-type" />
            </Field>
            <Field label="Duration (minutes)" testid="field-svc-duration">
              <Input type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="svc-duration" />
            </Field>
          </div>
          {(form.price_type === "range" || form.price_type === "fixed" || form.price_type === "starting_at") && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Min price ($)" testid="field-svc-min">
                <Input type="number" value={form.minimum_price} onChange={(e) => set("minimum_price", e.target.value)} data-testid="svc-min" />
              </Field>
              {form.price_type === "range" && (
                <Field label="Max price ($)" testid="field-svc-max">
                  <Input type="number" value={form.maximum_price} onChange={(e) => set("maximum_price", e.target.value)} data-testid="svc-max" />
                </Field>
              )}
            </div>
          )}
          <Button variant="outline" onClick={add} disabled={adding} data-testid="svc-add-btn">
            {adding ? "Adding…" : "Add service"}
          </Button>
        </div>
      </div>
      {services.length === 0 && (
        <StatusBadge tone="amber" data-testid="services-hint">Add at least one service so your AI can book jobs</StatusBadge>
      )}
    </StepShell>
  );
}
